<template>
    <div class="flex flex-column justify-content-center align-items-center w-full h-full">
        <Form class=" form">
            <div class="grid m-0 w-full align-items-center justify-content-center">
                <div class="col-12 md:col-6">
                    <div class="flex flex-column gap-3 pt-4 relative mb-3">
                        <label for="firstName" class="Label align-self-start">First Name</label>
                        <IconField class="w-full">
                            <InputIcon class="pi pi-user" />
                            <InputText id="firstName" name="firstName" type="text" placeholder="Enter first name here"
                                size="small" variant="filled" class="w-full" v-model="form.firstName" />
                        </IconField>
                        <small v-if="errors.firstName" class="error-text">{{ errors.firstName }}</small>
                    </div>
                    <div class="flex flex-column gap-3 pt-4 relative mb-3">
                        <label for="email" class="Label align-self-start">Email</label>
                        <IconField class="w-full">
                            <InputIcon class="pi pi-envelope" />
                            <InputText id="email" name="email" type="email" placeholder="Enter email here" size="small"
                                variant="filled" class="w-full" disabled v-model="form.email" />
                        </IconField>
                    </div>
                    <div class="flex flex-column gap-3 pt-4 relative mb-3">
                        <label for="NIC" class="Label align-self-start">NIC</label>
                        <IconField class="w-full">
                            <InputIcon class="pi pi-id-card" />
                            <InputText id="NIC" name="NIC" type="text" placeholder="Enter NIC here" size="small"
                                variant="filled" class="w-full" v-model="form.NIC" />
                        </IconField>
                        <small v-if="errors.NIC" class="error-text">{{ errors.NIC }}</small>
                    </div>
                </div>

                <div class="col-12 md:col-6">
                    <div class="flex flex-column gap-3 pt-4 relative mb-3">
                        <label for="lastName" class="Label align-self-start">Last Name</label>
                        <IconField class="w-full">
                            <InputIcon class="pi pi-user" />
                            <InputText id="lastName" name="lastName" type="text" placeholder="Enter last name here"
                                size="small" variant="filled" class="w-full" v-model="form.lastName" />
                        </IconField>
                        <small v-if="errors.lastName" class="error-text">{{ errors.lastName }}</small>
                    </div>
                    <div class="flex flex-column gap-3 pt-4 relative mb-3">
                        <label for="phone" class="Label align-self-start">Mobile Number</label>
                        <div class="flex gap-2 w-full">
                            <Select v-model="form.phoneCountryCode" :options="countryCodeOptions" optionLabel="name"
                                optionValue="code" placeholder="Code" variant="filled" size="small"
                                class="phone-country-select" />
                            <IconField class="w-full">
                                <InputIcon class="pi pi-phone" />
                                <InputText id="phone" name="phone" type="tel" placeholder="Enter mobile number here"
                                    size="small" variant="filled" class="w-full" v-model="form.phoneNumber"
                                    maxlength="15" @keypress="onlyAllowNumbers" />
                            </IconField>
                        </div>
                        <small v-if="errors.phone" class="error-text">{{ errors.phone }}</small>
                    </div>
                    <div class="flex flex-column gap-3 pt-4 relative mb-3">
                        <label for="address" class="Label align-self-start">Address</label>
                        <IconField class="w-full">
                            <InputIcon class="pi pi-map-marker" />
                            <InputText id="address" name="address" type="text" placeholder="Enter address here"
                                size="small" variant="filled" class="w-full" v-model="form.address" />
                        </IconField>
                        <small v-if="errors.address" class="error-text">{{ errors.address }}</small>
                    </div>
                </div>

                <div class="col-12 md:col-4">
                    <div class="flex flex-column gap-3 pt-4 relative mb-3">
                        <label for="dob" class="Label align-self-start">Date of Birth</label>
                        <IconField class="w-full">
                            <InputIcon class="pi pi-calendar" />
                            <DatePicker v-model="form.dob" dateFormat="dd/mm/yy" showIcon fluid iconDisplay="input"
                                variant="filled" size="small" class="w-full" />
                        </IconField>
                        <small v-if="errors.dob" class="error-text">{{ errors.dob }}</small>
                    </div>
                </div>

                <div class="col-12 md:col-4">
                    <div class="flex flex-column gap-3 pt-4 relative mb-3">
                        <label for="gender" class="Label align-self-start">Gender</label>
                        <Select v-model="form.gender" :options="gender" variant="filled" size="small"
                            optionLabel="label" placeholder="Select your gender" class="w-full" />
                        <small v-if="errors.gender" class="error-text">{{ errors.gender }}</small>
                    </div>
                </div>

                <div class="col-12 md:col-4">
                    <div class="flex flex-column gap-3 pt-4 relative mb-3">
                        <label for="nationality" class="Label align-self-start">Nationality</label>
                        <NationalitySelect id="nationality" v-model="form.nationality" label="" placeholder="Select your nationality" />
                    </div>
                </div>

                <div class="col-12 col-6  flex flex-column  gap-3 relative mb-3">
                    <label for="bio" class="Label align-self-start">Bio</label>
                    <Textarea v-model="form.bio" variant="filled" autoResize rows="5" cols="30" class="w-full" />
                    <!-- <small v-if="errors.bio" class="error-text">{{ errors.bio }}</small> -->
                </div>
                <div class="col-12 col-6 flex justify-content-between">
                    <div class=" align-items-center gap-3 ">
                        <Button label="Back" class="teal-button-transparent mt-6" @click="previousStep" />
                    </div>
                    <div class=" align-items-center gap-3 ">
                        <Button label="Next" class="teal-button mt-6" @click="handleRegister" />
                    </div>
                </div>
            </div>
        </Form>
    </div>
</template>

<script setup>
import { computed, defineEmits, reactive, ref, watch, onMounted } from "vue";
import { Form } from "@primevue/forms";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
// import Message from "primevue/message";
import Select from "primevue/select";
import { gender } from "@/utils/options";
import { cities } from "@/utils/cities";
import { countryCodeOptions } from "@/utils/countryCodeOptions";
import DatePicker from 'primevue/datepicker';
import Textarea from 'primevue/textarea';
import NationalitySelect from '@/components/common/NationalitySelect.vue';
import { useAuthStore } from "@/stores/authStore";

const authStore = useAuthStore();
const emit = defineEmits(["submit-personal-info", "previous-step"]);

const errors = ref({});

const computedEmail = computed(() => {
    return authStore.user.email;
});

const form = reactive({
    firstName: "",
    lastName: "",
    email: computedEmail,
    NIC: "",
    dob: null,
    phone: "",
    phoneCountryCode: "+94",
    phoneNumber: "",
    address: "",
    city: null,
    gender: null,
    nationality: "Sri Lanka",
    bio: "",
});

const parsePhoneFromProfile = (mobileNumber) => {
    if (!mobileNumber) return { phoneCountryCode: "+94", phoneNumber: "" };
    const numStr = String(mobileNumber);
    if (numStr.startsWith("+")) {
        const sortedCodes = [...countryCodeOptions.map(c => c.code)].sort((a, b) => b.length - a.length);
        for (const code of sortedCodes) {
            if (numStr.startsWith(code)) {
                return { phoneCountryCode: code, phoneNumber: numStr.slice(code.length) };
            }
        }
    }
    return { phoneCountryCode: "+94", phoneNumber: numStr };
};

onMounted(() => {
    const user = authStore.user;
    if (user) {
        const phone = parsePhoneFromProfile(user.mobileNumber);
        form.firstName = user.firstName || "";
        form.lastName = user.lastName || "";
        form.NIC = user.NIC || "";
        form.dob = user.dateOfBirth ? new Date(user.dateOfBirth) : null;
        form.phone = user.mobileNumber || "";
        form.phoneCountryCode = phone.phoneCountryCode;
        form.phoneNumber = phone.phoneNumber;
        form.address = user.address || "";
        form.city = cities.find(c => c.code === user.city) || null;
        form.gender = gender.find(g => g.value === user.gender) || null;
        form.bio = user.tutorBio || "";
        form.nationality = user.nationality || "Sri Lanka";
    }
});

// --- WATCHERS TO CLEAR ERRORS ON INPUT ---
watch(() => form.firstName, (val) => { if (val) errors.value.firstName = null; });
watch(() => form.lastName, (val) => { if (val) errors.value.lastName = null; });
watch(() => form.NIC, (val) => { if (val) errors.value.NIC = null; });
watch(() => form.dob, (val) => { if (val) errors.value.dob = null; });
watch(() => form.phoneNumber, (val) => { if (val) errors.value.phone = null; });
watch(() => form.address, (val) => { if (val) errors.value.address = null; });
watch(() => form.city,    (val) => { if (val) errors.value.city    = null; });
watch(() => form.gender,  (val) => { if (val) errors.value.gender  = null; });
// watch(() => form.bio, (val) => { if (val) errors.value.bio = null; });


const validateForm = () => {
    errors.value = {}; // Reset errors on each validation attempt

    if (!form.firstName) errors.value.firstName = "First name is required";
    if (!form.lastName) errors.value.lastName = "Last name is required";
    if (!form.NIC) errors.value.NIC = "NIC is required";
    if (!form.dob) errors.value.dob = "Date of Birth is required";
    if (!form.phoneNumber) errors.value.phone = "Mobile number is required";
    if (form.phoneNumber && (form.phoneNumber.length < 6 || form.phoneNumber.length > 15)) errors.value.phone = "Invalid mobile number";
    if (!form.address) errors.value.address = "Address is required";
    if (!form.gender)  errors.value.gender  = "Gender is required";
    // if (!form.bio) errors.value.bio = "Bio is required";

    return Object.keys(errors.value).length === 0; // Returns true if no errors
};

const sanitizePhoneNumber = () => {
    form.phoneNumber = form.phoneNumber.replace(/\D/g, "");
    form.phone = `${form.phoneCountryCode}${form.phoneNumber}`;
};

const handleRegister = async () => {
    sanitizePhoneNumber();

    // Validate the form first
    if (!validateForm()) {
        return; // Stop execution if validation fails
    }

    // Validation passed, proceed to create payload
    const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        NIC: form.NIC,
        dateOfBirth: form.dob,
        mobileNumber: form.phone,
        address: form.address,
        gender: form.gender.value,
        nationality: form.nationality,
        tutorBio: form.bio,
    };
    emit("submit-personal-info", payload);
};

const previousStep = () => {
    emit("previous-step");
};

const onlyAllowNumbers = (event) => {
    const char = String.fromCharCode(event.which)
    if (!/[0-9]/.test(char)) {
        event.preventDefault()
    }
}
</script>

<style scoped>
.form {
    width: 100%;
    min-width: 60vw;
}

@media (max-width: 768px) {
    .form {
        min-width: none;
    }
}

.phone-country-select {
    min-width: 140px;
    max-width: 180px;
}

.error-text {
    color: #dc2626;
    font-size: 0.85rem;
    position: absolute;
    top: 100%;
    margin-top: 0.5rem;
    left: 0;
    padding-left: 0.5rem;
}
</style>